import type { PropsWithChildren } from 'react';
import { createContext, useContext, useLayoutEffect, useMemo } from 'react';
import {
  getWidgetTokenStore,
  type WidgetTokenStore
} from './widget-token-store';

// A static widget ID for usage in local development only
export const LOCAL_WIDGET_ID = Symbol.for('local_widget_id');
// A static widget ID for usage in configurator only
export const CONFIGURATOR_WIDGET_ID = Symbol.for('configurator_widget_id');

const WidgetTokenContext = createContext<WidgetTokenStore | undefined>(
  undefined
);

interface WidgetTokenProviderProps extends PropsWithChildren {
  widgetId: string | symbol | undefined;
  widgetToken: string | undefined;
}

export function WidgetTokenProvider({
  children,
  widgetId,
  widgetToken
}: WidgetTokenProviderProps) {
  const store = useMemo(() => {
    if (!widgetId) {
      throw new Error('widgetId is required');
    }
    return getWidgetTokenStore(widgetId);
  }, [widgetId]);

  useLayoutEffect(() => {
    if (widgetToken) {
      store.set(widgetToken);
    }
  }, [widgetToken, store]);

  if (!widgetToken) {
    return null;
  }

  return (
    <WidgetTokenContext.Provider value={store}>
      {children}
    </WidgetTokenContext.Provider>
  );
}

export function useWidgetTokenStore(): WidgetTokenStore {
  const context = useContext(WidgetTokenContext);
  if (!context) {
    throw new Error(
      'useWidgetTokenStore must be used within a WidgetTokenProvider'
    );
  }
  return context;
}
